import { Practice } from '../types/practices/types';

/**
 * Constructs a complete address string from practice address fields
 * Handles null/undefined values gracefully and filters out empty strings
 * @param practice - The practice object containing address fields
 * @returns A formatted address string, or empty string if no address data
 */
export const constructPracticeAddress = (practice: Practice | undefined): string => {
  if (!practice) return '';

  const addressParts = [
    practice.address1,
    practice.address2,
    practice.city,
    practice.state,
    practice.zip
  ].filter(part => part && part.trim() !== '' && part.trim().toLowerCase() !== 'null');

  if (addressParts.length === 0) return '';

  return addressParts.join(', ');
};

/**
 * Constructs a complete address string from practice address fields for Google Maps
 * Similar to constructPracticeAddress but optimized for URL encoding
 * @param practice - The practice object containing address fields
 * @returns A formatted address string for Google Maps, or empty string if no address data
 */
export const constructPracticeAddressForMaps = (practice: Practice | undefined): string => {
  if (!practice) return '';

  const addressParts = [
    practice.address1,
    practice.address2,
    practice.city,
    practice.state,
    practice.zip
  ].filter(part => part && part.trim() !== '' && part.trim().toLowerCase() !== 'null');

  if (addressParts.length === 0) return '';

  return addressParts.join(', ');
};

/**
 * Sort rank for a practice's PAC status: on PAC first, unknown next, not on PAC last.
 * practiceOnPac may arrive as a boolean or the strings 'true'/'false' depending on
 * the API source, so both forms are handled (mirrors PracticeStatusBadge normalization).
 */
const pacSortRank = (practice: Practice): number => {
  const { practiceOnPac } = practice;
  if (practiceOnPac === true || practiceOnPac === 'true') return 0;
  if (practiceOnPac === false || practiceOnPac === 'false') return 2;
  return 1;
};

/**
 * Sorts practices so on-PAC practices appear before not-on-PAC ones,
 * preserving the existing relative order within each group.
 * Prevents agents from seeing a "Not on PAC" practice at the top of a
 * provider's practice list when an on-PAC practice exists.
 *
 * @param practices - Array of Practice objects
 * @returns A new sorted array (input is not mutated)
 */
export const sortPracticesByPacStatus = (
  practices: Practice[],
): Practice[] =>
  [...practices].sort((a, b) => pacSortRank(a) - pacSortRank(b));

/**
 * Maps related practices to the format expected by ProviderAbsencesCard.
 * Extracts only the fields needed by the absences card component.
 *
 * @param practices - Array of Practice objects (usually from provider.relatedPractices)
 * @returns Array of practice objects with only id, name, timezone, and state fields
 */
export const mapPracticesForAbsencesCard = (
  practices?: Practice[]
): Array<{ id: string; name: string; timezone?: string | null; state?: string | null }> => {
  if (!practices || practices.length === 0) {
    return [];
  }

  return practices.map((practice) => ({
    id: practice.id,
    name: practice.name,
    timezone: practice.timezone,
    state: practice.state,
  }));
};
